import {
  Box,
  Dialog,
  IconButton,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useFeatureFlag, SPLITS } from '@/helpers/useFeatureFlag'
import ClearIcon from '@mui/icons-material/Clear'
import React from 'react'
import TagFiltersMui from './tag-filters-mui'
import { useTranslation } from 'react-i18next'
import { tagGroupNames, VendorSearchState } from '../../states/useVendorSearchState'

type Props = {
  filterOpen: boolean
  toggleFilterOpen: () => void
  filters: VendorSearchState['filters']
  search: VendorSearchState['search']
  stats: VendorSearchState['stats']
  searching: boolean
}

const SupplierFilters: React.FC<Props> = ({
  filterOpen,
  toggleFilterOpen,
  search,
  filters,
  searching,
  stats,
}) => {
  const countrySpecificFiltersFlagOn = useFeatureFlag(SPLITS.COUNTRY_SPECIFIC_FILTERS_GM)
  const showDiversityTags = useFeatureFlag(SPLITS.SHOW_DIVERSITY_TAGS_GM)
  const showBestForTags = useFeatureFlag(SPLITS.SHOW_BEST_FOR_TAGS_GM)

  const theme = useTheme()
  const { t } = useTranslation()
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('md'))

  type OrderBy = 'position' | 'label'

  const getOrderBy = (condition: boolean, defaultValue: OrderBy): OrderBy => {
    return condition ? 'position' : defaultValue
  }

  const tagFiltersConfig = [
    ...(showBestForTags
      ? [
          {
            tagType: tagGroupNames.BestFor,
            specificToCountry: countrySpecificFiltersFlagOn,
            orderBy: getOrderBy(countrySpecificFiltersFlagOn, 'label'),
          },
        ]
      : []),
    {
      tagType: tagGroupNames.Occasion,
      specificToCountry: countrySpecificFiltersFlagOn,
      orderBy: getOrderBy(countrySpecificFiltersFlagOn, 'label'),
    },
    {
      tagType: tagGroupNames.Cuisine,
      specificToCountry: countrySpecificFiltersFlagOn,
      orderBy: 'position' as OrderBy,
    },
    {
      tagType: tagGroupNames.Dietary,
      orderBy: 'label' as OrderBy,
      useName: true,
    },
    {
      tagType: tagGroupNames.KeyDates,
      specificToCountry: countrySpecificFiltersFlagOn,
      orderBy: getOrderBy(countrySpecificFiltersFlagOn, 'label'),
    },
  ]

  if (showDiversityTags) {
    tagFiltersConfig.push({
      tagType: tagGroupNames.DiversityOfOwnership,
      specificToCountry: countrySpecificFiltersFlagOn,
      orderBy: getOrderBy(countrySpecificFiltersFlagOn, 'label'),
    })
  }

  const TagSections = (
    <Box sx={{ width: '100%' }}>
      <Stack spacing={1.5}>
        {tagFiltersConfig.map((filter, index) => (
          <TagFiltersMui
            key={`${filter.tagType.key}-${filter.tagType.id}`}
            tagType={filter.tagType}
            stats={stats}
            search={search}
            filters={filters}
            searching={searching}
            defaultOpen={index === 0}
            orderBy={filter.orderBy}
            specificToCountry={filter.specificToCountry}
          />
        ))}
      </Stack>
    </Box>
  )

  return <>{TagSections}</>
}

export default SupplierFilters
